/**
 * Wave codex-r8-legacy-redirects · 2026-05-19
 *
 * `RedirectWithToast` replaces bare `<Navigate replace>` for legacy bookmark
 * redirects. The user-facing problem (Codex R8 finding):
 *
 *   "21 redirects inesperados/legados — /cotacoes→/compras, /rfi→/pranchas,
 *    /reports+/medicoes→/finance, /cronograma→timeline. Tecnicamente ok, mas
 *    mentalmente bagunça."
 *
 * If a PM clicks a notification email link to `/projects/X/medicoes` and
 * silently lands on `/projects/X/finance`, the address bar changes under
 * them with no signal — "fluidez e facilidade" (OPERATING-MANUAL §filosofia)
 * is broken because the system lied by omission about where it took them.
 *
 * Solution: render a tiny inline banner overlay for ~1.5s announcing the
 * rename before performing the `navigate(to, { replace: true })`. Bookmarks
 * stay alive (we still redirect), and the user learns the new URL.
 *
 * Why not a global toast system?
 *   The repo intentionally avoids a global toast layer (CLAUDE.md "No modals
 *   rule" + Wave 100 v3 chrome only allows `CommandBar` / `ConfirmDialog`
 *   as floating UI). A self-contained banner inside the route element keeps
 *   the chrome surface unchanged.
 *
 * Accessibility:
 *   - `role="status"` + `aria-live="polite"` so screen readers announce.
 *   - Banner is visible content, not a modal — no focus trap, no overlay
 *     blocking interaction. The page underneath is the destination page
 *     mounting in parallel; the banner just floats top-center.
 *
 * Hash/query preservation:
 *   Incoming `location.search` and `location.hash` are carried forward by
 *   default so old bookmarks keep their intent. If `to` declares its own
 *   query or hash (for example `?view=audit` or `#medicoes-section`), the
 *   explicit canonical intent wins while any non-conflicting search params
 *   from the legacy URL are still preserved.
 */

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useT } from '@/hooks/useT';

interface RedirectWithToastProps {
  /** Canonical route to navigate to (may include query / hash). */
  to: string;
  /** Human-readable label of the legacy path, e.g. "/cotacoes". */
  fromLabel: string;
  /** Human-readable label of the destination, e.g. "Compras". */
  toLabel: string;
  /**
   * Delay before the actual navigate. Default 1500 ms — long enough for a
   * sighted user to read the banner, short enough to feel like "the page
   * is just loading". Set to 0 in tests for fast assertions.
   */
  delayMs?: number;
  /**
   * wave-merger-context-banner · 2026-05-21
   *
   * Optional stable id for the legacy→canonical merge. When present, the
   * navigate call carries `location.state.routeMerger` so the destination
   * page can render a small dismissable in-page contextual banner ("você
   * está em Compras, aba Cotações"). JP's feedback was that the 1.5s
   * floating toast disappears too fast — once the destination loads, the
   * user has no signal *why* the URL changed under them. The state-based
   * handoff is invisible to URL/pathname asserting tests (which JP keeps
   * pinned source-level), so existing source contracts survive untouched.
   *
   * Convention: lowercase, hyphenless. Examples: `cotacoes`, `medicoes`,
   * `rfi`, `photos`, `logs`, `reports`. Each destination decides which
   * keys it knows how to render and ignores the rest.
   */
  mergerKey?: string;
}

/**
 * wave-merger-context-banner · 2026-05-21.
 * Shape attached to `location.state` when a `RedirectWithToast` carries
 * a `mergerKey`. Destination pages read this via `useLocation().state`
 * and only render the in-page banner once per dismissable session.
 */
export interface RouteMergerState {
  routeMerger: {
    key: string;
    fromPath: string;
    parentLabel: string;
  };
}

function splitTarget(to: string) {
  const hashIndex = to.indexOf('#');
  const beforeHash = hashIndex >= 0 ? to.slice(0, hashIndex) : to;
  const hash = hashIndex >= 0 ? to.slice(hashIndex) : '';
  const searchIndex = beforeHash.indexOf('?');
  if (searchIndex < 0) {
    return { pathname: beforeHash, search: '', hash };
  }
  return {
    pathname: beforeHash.slice(0, searchIndex),
    search: beforeHash.slice(searchIndex),
    hash,
  };
}

function mergeSearch(targetSearch: string, currentSearch: string): string {
  if (!currentSearch) return targetSearch;
  if (!targetSearch) return currentSearch;

  const targetParams = new URLSearchParams(targetSearch);
  const currentParams = new URLSearchParams(currentSearch);
  const explicitTargetKeys = new Set(targetParams.keys());

  currentParams.forEach((value, key) => {
    if (!explicitTargetKeys.has(key)) {
      targetParams.append(key, value);
    }
  });

  const merged = targetParams.toString();
  return merged ? `?${merged}` : '';
}

function preserveRedirectIntent(to: string, currentSearch: string, currentHash: string): string {
  const target = splitTarget(to);
  const search = mergeSearch(target.search, currentSearch);
  const hash = target.hash || currentHash;
  return `${target.pathname}${search}${hash}`;
}

export function RedirectWithToast({
  to,
  fromLabel,
  toLabel,
  delayMs = 1500,
  mergerKey,
}: RedirectWithToastProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [visible, setVisible] = useState(true);
  const { locale } = useT();
  const isEn = locale === 'en';

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setVisible(false);
      const target = preserveRedirectIntent(to, location.search, location.hash);
      if (mergerKey) {
        const state: RouteMergerState = {
          routeMerger: {
            key: mergerKey,
            fromPath: fromLabel,
            parentLabel: toLabel,
          },
        };
        navigate(target, { replace: true, state });
      } else {
        navigate(target, { replace: true });
      }
    }, delayMs);
    return () => window.clearTimeout(handle);
  }, [navigate, to, delayMs, mergerKey, fromLabel, toLabel, location.search, location.hash]);

  if (!visible) return null;

  return (
    <div
      // role="status" + aria-live polite: SR announces without interrupting
      role="status"
      aria-live="polite"
      data-testid="legacy-redirect-toast"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-2rem)]"
    >
      <div className="rounded-lg border border-line bg-surface px-4 py-3 shadow-md flex items-start gap-3">
        <svg
          className="w-5 h-5 text-accent shrink-0 mt-0.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path d="M13 5l7 7-7 7M5 12h15" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div className="text-sm text-ink flex-1">
          <strong className="font-medium">{isEn ? 'Page renamed.' : 'Página renomeada.'}</strong>{' '}
          <span className="text-muted">
            {isEn
              ? <>{fromLabel} now lives at <span className="text-ink font-mono">{toLabel}</span>.</>
              : <>{fromLabel} agora vive em <span className="text-ink font-mono">{toLabel}</span>.</>}
          </span>
          <div className="text-xs text-muted mt-0.5">{isEn ? 'Update your bookmarks.' : 'Atualize seus favoritos.'}</div>
        </div>
      </div>
    </div>
  );
}
